import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Anchor,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowUpRight,
  IconBolt,
  IconBrandWindows,
  IconClock,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import "./VersionsPage.scss";
import ReleaseBadges from "./ReleaseBadges";
import VersionControlCard from "./VersionControlCard";
import { useReleaseNotes } from "./useReleaseNotes";
import {
  currentDesktop,
  DESKTOP_WINDOWS_DOWNLOAD_URL,
  displayVersion,
  isOfflineRelease,
  offersWindowsDownload,
  releaseCopy,
} from "./releaseCatalog";

export default function VersionDetailPage() {
  const { version } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { releases: releaseNotes, loading, latestOffline } = useReleaseNotes();
  const release = releaseNotes.find((r) => r.version === version);

  if (!release) {
    return (
      <div className="release-page">
        <Container size="xl" py="xl">
          <Stack gap="md">
            <Title order={2}>
              {loading ? t("releaseNotes.loadingVersion") : "Version not found"}
            </Title>
            <Text>
              {loading
                ? t("releaseNotes.checkingReleases")
                : `No release notes available for ${version}.`}
            </Text>
            <Button
              variant="light"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate("/versions")}
              style={{ alignSelf: "flex-start" }}
            >
              Back to all versions
            </Button>
          </Stack>
        </Container>
      </div>
    );
  }

  const copy = releaseCopy(release, t);
  const current = currentDesktop(latestOffline);

  return (
    <div className="release-page">
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/versions")}
            style={{ alignSelf: "flex-start" }}
          >
            All versions
          </Button>

          {release.heroImage ? (
            <section className="release-hero">
              <img
                src={release.heroImage.url}
                alt={release.heroImage.alt}
                className="release-hero__image"
              />
            </section>
          ) : null}

          {!isOfflineRelease(release) ? <VersionControlCard /> : null}

          <Card className="release-card" radius="xl" padding="xl">
            <div className="release-card__header">
              <div>
                <Group gap="sm" mb="xs">
                  <Title order={2}>{displayVersion(release)}</Title>
                  <ReleaseBadges release={release} current={current} />
                </Group>
                <Text className="release-card__summary">{copy.summary}</Text>
              </div>
              {copy.availability || offersWindowsDownload(release, current) ? (
                <Stack gap="sm">
                  {copy.availability ? (
                    <div className="release-card__meta">
                      <ThemeIcon variant="light" color="teal" radius="xl" size={38}>
                        <IconClock size={18} />
                      </ThemeIcon>
                      <Text>{copy.availability}</Text>
                    </div>
                  ) : null}
                  {offersWindowsDownload(release, current) ? (
                    <Button
                      component="a"
                      href={DESKTOP_WINDOWS_DOWNLOAD_URL}
                      rel="noreferrer"
                      color="orange"
                      radius="xl"
                      leftSection={<IconBrandWindows size={18} />}
                    >
                      {t("releaseNotes.downloadWindows")}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </div>

            <div className="release-card__sections">
              {release.sections.map((section) => (
                <div
                  key={section.title}
                  className={`release-section${section.fullWidth ? " release-section--full" : ""}`}
                >
                  <Group gap="xs" mb="sm">
                    <ThemeIcon variant="light" color="blue" radius="xl" size={30}>
                      <IconBolt size={16} />
                    </ThemeIcon>
                    <Title order={4}>{section.title}</Title>
                  </Group>

                  <ul className="release-section__list">
                    {section.items.map((item, idx) => {
                      if (typeof item === "string") {
                        return (
                          <li key={idx}>
                            <Text>{item}</Text>
                          </li>
                        );
                      }
                      return (
                        <li key={idx} className="release-section__item--detailed">
                          {item.heading ? (
                            <Text className="release-section__item-heading">{item.heading}</Text>
                          ) : null}
                          {item.text ? (
                            <Text className="release-section__item-text">{item.text}</Text>
                          ) : null}
                          {item.image ? (
                            <figure
                              className={`release-section__item-image release-section__item-image--${item.imageSize ?? "full"}`}
                            >
                              <img src={item.image.url} alt={item.image.alt} loading="lazy" />
                            </figure>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>

                  {section.imageGroups?.length ? (
                    <div className="release-section__media">
                      {section.imageGroups.map((group, index) => (
                        <div
                          key={group.title ?? `${section.title}-${index}`}
                          className="release-image-group"
                        >
                          {group.title ? (
                            <Text className="release-image-group__title">{group.title}</Text>
                          ) : null}

                          <div
                            className={`release-image-grid release-image-grid--${Math.min(group.images.length, 3)}`}
                          >
                            {group.images.map((image) => (
                              <figure key={image.url} className="release-image">
                                <img src={image.url} alt={image.alt} loading="lazy" />
                                <figcaption>{image.alt}</figcaption>
                              </figure>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {release.links?.length ? (
              <Group gap="md" mt="lg">
                {release.links.map((link) => (
                  <Anchor
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="release-card__link"
                  >
                    {link.label}
                    <IconArrowUpRight size={16} />
                  </Anchor>
                ))}
              </Group>
            ) : null}
          </Card>
        </Stack>
      </Container>
    </div>
  );
}
